import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';

import {
    type ScheduleDayGroup,
    type TimeboxedScheduleSegment,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { getCalendarMonthGridDayKeys } from '@apps/web/components/shifts_calendar/calendar_model';
import {
    dateKeyToCalendarDate,
    formatCompactTime,
} from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';

type ChipStatus = 'available' | 'unavailable' | 'shift' | 'open';

const CHIP_CLASS_BY_STATUS: Record<ChipStatus, string> = {
    available:
        'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
    unavailable: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
    shift: 'bg-secondary text-secondary-foreground',
    open: 'bg-destructive/10 text-destructive font-medium',
};

const LEGEND_DOT_CLASS_BY_STATUS: Record<ChipStatus, string> = {
    available: 'bg-green-500',
    unavailable: 'bg-red-500',
    shift: 'bg-secondary-foreground/60',
    open: 'bg-destructive',
};

const LEGEND_LABEL_BY_STATUS: Record<ChipStatus, string> = {
    available: 'Avail',
    unavailable: 'Busy',
    shift: 'Shift',
    open: 'Open',
};

const LEGEND_TITLE_BY_STATUS: Record<ChipStatus, string> = {
    available: 'Available',
    unavailable: 'Unavailable',
    shift: 'Shift',
    open: 'Open',
};

function getSegmentChipStatus(segment: TimeboxedScheduleSegment): ChipStatus {
    if (segment.kind === 'shift') {
        return segment.open ? 'open' : 'shift';
    }
    return segment.status;
}

function getChipTimeLabel(
    segment: TimeboxedScheduleSegment,
    timezone: string,
): string {
    const startLabel = formatCompactTime(segment.startTime, timezone);
    const endLabel = formatCompactTime(segment.endTime, timezone);
    // A midnight-to-midnight segment (day slices clamp to day bounds, and
    // synced entries often end at 11:59 PM) reads better as "All day".
    if (startLabel === '12a' && (endLabel === '12a' || endLabel === '11:59p')) {
        return 'All day';
    }
    return `${startLabel}–${endLabel}`;
}

function getChipLabel(
    segment: TimeboxedScheduleSegment,
    timezone: string,
): string {
    const timeLabel = getChipTimeLabel(segment, timezone);
    // Shifts carry the patient so a scheduled assignment is distinguishable
    // from an availability block at a glance; truncation trims the tail.
    return segment.kind === 'shift'
        ? `${timeLabel} · ${segment.details}`
        : timeLabel;
}

function getChipTooltip(segment: TimeboxedScheduleSegment): string {
    if (segment.kind === 'shift') {
        return `Shift ${segment.timeLabel} · ${segment.details}`;
    }
    const statusLabel =
        segment.status === 'unavailable' ? 'Unavailable' : 'Available';
    return segment.sourceLabel
        ? `${statusLabel} ${segment.timeLabel} · ${segment.sourceLabel}`
        : `${statusLabel} ${segment.timeLabel}`;
}

export function AvailabilityMonthGrid({
    monthStartKey,
    todayKey,
    timezone,
    dayGroups,
    onShiftClick,
    hideLegend = false,
}: {
    readonly monthStartKey: string;
    readonly todayKey: string;
    readonly timezone: string;
    readonly dayGroups: ReadonlyArray<
        ScheduleDayGroup<TimeboxedScheduleSegment>
    >;
    // When provided, shift chips become buttons; receives the shift id.
    readonly onShiftClick?: (shiftId: string) => void;
    readonly hideLegend?: boolean;
}) {
    const gridDayKeys = useMemo(
        () => getCalendarMonthGridDayKeys(monthStartKey),
        [monthStartKey],
    );
    const segmentsByDayKey = useMemo(
        () => new Map(dayGroups.map((group) => [group.key, group.items])),
        [dayGroups],
    );
    const monthPrefix = monthStartKey.slice(0, 7);

    return (
        <div className="space-y-2">
            <div
                className="border-border grid grid-cols-7 overflow-hidden rounded-lg border"
                role="grid"
                aria-label="Month availability grid"
                data-testid="availability-month-grid"
            >
                {gridDayKeys.slice(0, 7).map((dayKey) => (
                    <div
                        key={dayKey}
                        className="bg-muted/30 text-muted-foreground px-1 py-1.5 text-center text-[10px] font-semibold tracking-wide uppercase"
                    >
                        {formatInTimeZone(
                            dateKeyToCalendarDate(dayKey, timezone),
                            timezone,
                            'EEE',
                        )}
                    </div>
                ))}
                {gridDayKeys.map((dayKey, dayIndex) => {
                    const isOutsideMonth = !dayKey.startsWith(monthPrefix);
                    const segments = isOutsideMonth
                        ? []
                        : (segmentsByDayKey.get(dayKey) ?? []);
                    const isToday = dayKey === todayKey;
                    return (
                        <div
                            key={dayKey}
                            className={cn(
                                'border-border/70 min-h-12 min-w-0 border-t border-r px-1 py-1',
                                dayIndex % 7 === 6 && 'border-r-0',
                                isOutsideMonth && 'bg-muted/20',
                            )}
                            data-testid="availability-month-day-cell"
                            data-date-key={dayKey}
                            data-outside-month={isOutsideMonth || undefined}
                        >
                            <span
                                className={cn(
                                    'inline-block rounded-full px-1 text-xs font-semibold tabular-nums',
                                    isOutsideMonth &&
                                        'text-muted-foreground/60 font-normal',
                                    isToday &&
                                        'bg-primary text-primary-foreground',
                                )}
                            >
                                {Number(dayKey.slice(8))}
                                {isToday ? (
                                    <span className="sr-only"> Today</span>
                                ) : null}
                            </span>
                            {segments.map((segment) => {
                                const chipClassName = cn(
                                    'mt-0.5 block w-full truncate rounded-sm px-1 text-left text-[10px] leading-4',
                                    CHIP_CLASS_BY_STATUS[
                                        getSegmentChipStatus(segment)
                                    ],
                                );
                                if (
                                    segment.kind === 'shift' &&
                                    onShiftClick !== undefined
                                ) {
                                    return (
                                        <button
                                            key={segment.id}
                                            type="button"
                                            className={cn(
                                                chipClassName,
                                                'cursor-pointer hover:opacity-80',
                                            )}
                                            title={getChipTooltip(segment)}
                                            onClick={() =>
                                                onShiftClick(segment.parentId)
                                            }
                                        >
                                            {getChipLabel(segment, timezone)}
                                        </button>
                                    );
                                }
                                return (
                                    <span
                                        key={segment.id}
                                        className={chipClassName}
                                        title={getChipTooltip(segment)}
                                    >
                                        {getChipLabel(segment, timezone)}
                                    </span>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            {!hideLegend && <AvailabilityLegend />}
        </div>
    );
}

export function AvailabilityLegend({
    className,
}: {
    readonly className?: string;
}) {
    return (
        <div
            className={cn(
                'text-muted-foreground flex items-center gap-2 text-[10px] leading-none',
                className,
            )}
        >
            {(['available', 'unavailable', 'shift'] as Array<ChipStatus>).map(
                (status) => (
                    <span
                        key={status}
                        className="flex items-center gap-0.5"
                        title={LEGEND_TITLE_BY_STATUS[status]}
                    >
                        <span
                            className={cn(
                                'size-1.5 rounded-full',
                                LEGEND_DOT_CLASS_BY_STATUS[status],
                            )}
                            aria-hidden={true}
                        />
                        {LEGEND_LABEL_BY_STATUS[status]}
                    </span>
                ),
            )}
        </div>
    );
}
